import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Phone, MapPin, Mail, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

const LOGO_URL = '/images/logo.png';
const HERO_BG = '/images/bg-home.png';
const PRODUCT_IMG = '/images/sample-product.png';
const DELIVERY_IMG = '/images/delivery-image.png';

export default function Homepage() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav activeItem="Home" />

      <section className="relative py-32" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} data-testid="hero-section">
        <div className="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-white mb-4" data-testid="hero-overline">PURE. CLEAN. RELIABLE.</p>
            <h1 className="text-5xl sm:text-6xl tracking-tighter font-light text-brand-primary mb-6 font-outfit" data-testid="hero-title">
              Hydrate With Confidence
            </h1>
            <p className="text-base leading-relaxed text-brand-primary font-manrope mb-8" data-testid="hero-description">
              Premium RO-filtered water delivered to your doorstep. Experience purity in every drop with ZAN Aqua's advanced filtration technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/923017196699"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-brand-accent text-white hover:bg-brand-accent-hover px-8 py-3 shadow-sm transition-all duration-200 flex items-center justify-center"
                data-testid="hero-order-button"
              >
                Order Now via WhatsApp
                <ChevronRight className="ml-2 h-5 w-5" />
              </a>
              <Link
                to="/products"
                className="border border-brand-primary text-brand-primary hover:bg-slate-50 px-8 py-3 transition-all duration-200 text-center"
                data-testid="hero-products-button"
              >
                View Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-accent mb-4">OUR SERVICES</p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-brand-primary font-outfit">What We Offer</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              data-testid="service-refill"
            >
              <Droplets className="h-12 w-12 text-brand-accent mb-4" />
              <h3 className="text-xl sm:text-2xl tracking-tight font-medium text-slate-800 mb-3">19L Bottle Refill</h3>
              <p className="text-base leading-relaxed text-slate-600 font-manrope">Home delivery and in-store refill services for your 19-liter bottles. Fresh, pure water on demand.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white p-8 border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              data-testid="service-bottles"
            >
              <img src={PRODUCT_IMG} alt="Bottles" className="h-12 mb-4" />
              <h3 className="text-xl sm:text-2xl tracking-tight font-medium text-slate-800 mb-3">New & Used Bottles</h3>
              <p className="text-base leading-relaxed text-slate-600 font-manrope">High-quality 19L bottles available for purchase. New and certified used bottles at competitive prices.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              data-testid="service-delivery"
            >
              <MapPin className="h-12 w-12 text-brand-accent mb-4" />
              <h3 className="text-xl sm:text-2xl tracking-tight font-medium text-slate-800 mb-3">Home Delivery</h3>
              <p className="text-base leading-relaxed text-slate-600 font-manrope">Free delivery within 5 KM radius. Fast, reliable service to your doorstep.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="products-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-accent mb-4">PREMIUM QUALITY</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-brand-primary font-outfit mb-6">Complete Water Solutions</h2>
              <p className="text-base leading-relaxed text-slate-600 font-manrope mb-6">
                Beyond refills, we offer a complete range of water solutions including PET bottles (0.5L and 1.5L) and essential accessories like dispensers, stands, and more.
              </p>
              <Link
                to="/products"
                className="inline-flex items-center text-brand-accent hover:text-brand-accent-hover transition-colors duration-200"
                data-testid="view-all-products-link"
              >
                View All Products
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={PRODUCT_IMG} alt="Products" className="w-full" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" data-testid="delivery-info-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img src={DELIVERY_IMG} alt="Delivery" className="w-full" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-accent mb-4">FAST & RELIABLE</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-brand-primary font-outfit mb-6">Delivery Within 5 KM</h2>
              <p className="text-base leading-relaxed text-slate-600 font-manrope mb-6">
                We provide prompt home delivery service within a 5 KM radius. Order via WhatsApp and get fresh, pure water delivered to your home or office.
              </p>
              <div className="flex items-center space-x-4">
                <Phone className="h-6 w-6 text-brand-accent" />
                <div>
                  <p className="text-sm text-slate-500">Order Now</p>
                  <a href="https://wa.me/923017196699" className="text-lg font-medium text-brand-primary hover:text-brand-accent transition-colors duration-200" data-testid="whatsapp-link">
                    +92-301-7196699
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />

    </div>
  );
}